/**
 * Session Manager
 * Handles session expiration detection and modal display
 */

import logger from './logger';

class SessionManager {
  constructor() {
    this.listeners = [];
    this.isSessionExpired = false;
  }

  /**
   * Register a listener for session expiration events
   * @param {function} callback - Function to call when session expires
   */
  onSessionExpired(callback) {
    this.listeners.push(callback);
  }

  /**
   * Remove a session expiration listener
   * @param {function} callback - The callback to remove
   */
  offSessionExpired(callback) {
    this.listeners = this.listeners.filter(listener => listener !== callback);
  }

  /**
   * Trigger session expiration event
   * This will notify all registered listeners
   * @param {string} message - Optional custom message for the expiration
   */
  triggerSessionExpired(message = null) {
    if (this.isSessionExpired) {
      // Prevent multiple triggers
      return;
    }

    this.isSessionExpired = true;
    this.expirationMessage = message;
    logger.warn(`[SessionManager] Session expired${message ? ` (${message})` : ''} - notifying listeners`);
    
    this.listeners.forEach(listener => {
      try {
        listener(message);
      } catch (error) {
        logger.error('[SessionManager] Error in session expired listener:', error);
      }
    });
  }

  /**
   * Get the expiration message if set
   * @returns {string|null}
   */
  getExpirationMessage() {
    return this.expirationMessage;
  }

  /**
   * Reset session expiration state
   */
  resetSessionExpired() {
    this.isSessionExpired = false;
    this.expirationMessage = null;
  }

  /**
   * Check if error indicates session expiration
   * @param {Error|Object} error - The error to check
   * @returns {boolean} - True if error indicates expired session
   */
  isSessionExpiredError(error) {
    if (!error) return false;

    const errorMessage = (error.message || error.error || '').toLowerCase();
    const errorBody = error.body ? JSON.stringify(error.body).toLowerCase() : '';
    const errorUrl = error.url || error.config?.url || '';

    // Check for backend API 401 authentication errors
    // These indicate the user's login session/token has expired
    if (error.status === 401) {
      // Check for authentication-related error messages
      const authExpiredIndicators = [
        'authentication failed',
        'token expired',
        'session expired',
        'invalid token',
        'unauthorized',
        'jwt expired',
        'jwt malformed',
        'not authenticated',
        'please login',
        'login required',
        'access denied'
      ];

      const hasAuthError = authExpiredIndicators.some(indicator =>
        errorMessage.includes(indicator) || errorBody.includes(indicator)
      );

      // For 401 errors, trigger session expired unless it's specifically a Google OAuth error
      // that should be handled separately
      const isGoogleOAuthError = errorUrl.includes('google') ||
                                errorUrl.includes('mybusiness') ||
                                errorUrl.includes('oauth2');

      if (!isGoogleOAuthError || hasAuthError) {
        logger.warn('[SessionManager] Backend API 401 error detected - session expired');
        return true;
      }
    }

    // Check for Google OAuth-specific session expiration indicators
    const isGoogleOAuthError = errorUrl.includes('google') ||
                              errorUrl.includes('mybusiness') ||
                              errorUrl.includes('oauth2');

    // Check for Google-specific session expiration indicators
    const googleExpiredIndicators = [
      'google session expired',
      'google token expired',
      'invalid_grant',
      'invalid grant',
      'gmb profile expired',
      'error loading gmb profile'
    ];

    // Check if status is 401 AND it's a Google API call
    if (error.status === 401 && isGoogleOAuthError) {
      return true;
    }

    // Check error message and body for Google-specific expiration indicators
    return googleExpiredIndicators.some(indicator =>
      errorMessage.includes(indicator) || errorBody.includes(indicator)
    );
  }

  /**
   * Handle API error and check if it's a session expiration
   * @param {Error|Object} error - The error to handle
   * @returns {boolean} - True if session expired, false otherwise
   */
  handleApiError(error) {
    if (this.isSessionExpiredError(error)) {
      this.triggerSessionExpired();
      return true;
    }
    return false;
  }
}

// Create singleton instance
const sessionManager = new SessionManager();

export default sessionManager;

